import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SchemaOrg from "@/components/feature/SchemaOrg";
import AnimeCard from "@/components/base/AnimeCard";
import { animeList, categories } from "@/mocks/animeData";

const formSubmitUrl = "http://yuanyunge.cn/";

const sortOptions = [
  { value: "default", label: "默认排序" },
  { value: "rating", label: "评分最高" },
  { value: "views", label: "播放最多" },
  { value: "newest", label: "最新更新" },
];

const ITEMS_PER_PAGE = 12;

export default function CategoryPage() {
  const { slug } = useParams();
  const [sortBy, setSortBy] = useState("default");
  const [currentPage, setCurrentPage] = useState(1);

  const categoryName = categories.find((c) => c.slug === slug)?.name || "全部";

  const filtered = useMemo(() => {
    let items = [...animeList];
    if (slug && slug !== "action-adventure") {
      const catName = categories.find((c) => c.slug === slug)?.name;
      if (catName) {
        items = items.filter((a) => a.category === catName);
      }
    }

    switch (sortBy) {
      case "rating":
        items.sort((a, b) => b.rating - a.rating);
        break;
      case "views":
        items.sort((a, b) => parseFloat(b.views) - parseFloat(a.views));
        break;
      case "newest":
        items.sort(
          (a, b) =>
            new Date(b.updateTime).getTime() -
            new Date(a.updateTime).getTime()
        );
        break;
      default:
        break;
    }

    return items;
  }, [slug, sortBy]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: categoryName },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${categoryName} - 樱花动漫网`,
    description: `樱花动漫网${categoryName}分类页面，海量免费高清${categoryName}在线观看。`,
    url: `http://yuanyunge.cn/category/${slug}`,
    inLanguage: "zh-CN",
    isPartOf: {
      "@type": "WebSite",
      name: "樱花动漫网",
      url: "http://yuanyunge.cn/",
    },
    about: {
      "@type": "Thing",
      name: categoryName,
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        <SchemaOrg type="WebPage" data={jsonLd} />
        <Breadcrumb items={breadcrumbItems} />

        <section className="py-6 md:py-10">
          <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                {categoryName}
              </h1>
              <p className="text-foreground-500 text-sm mt-2">
                共 {filtered.length} 部作品，每日同步更新
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      slug === cat.slug
                        ? "bg-primary-500 text-white"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <label className="text-xs text-foreground-500 whitespace-nowrap">
                  排序：
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1.5 rounded-lg bg-background-100 text-sm text-foreground-700 border border-background-200 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
                >
                  {sortOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {paginated.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {paginated.map((anime) => (
                    <AnimeCard key={anime.id} anime={anime} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    <button
                      onClick={() =>
                        setCurrentPage((p) => Math.max(1, p - 1))
                      }
                      disabled={currentPage === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      aria-label="上一页"
                    >
                      <i className="ri-arrow-left-s-line text-lg"></i>
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                            page === currentPage
                              ? "bg-primary-500 text-white"
                              : "bg-background-100 text-foreground-600 hover:bg-background-200"
                          }`}
                        >
                          {page}
                        </button>
                      )
                    )}

                    <button
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      aria-label="下一页"
                    >
                      <i className="ri-arrow-right-s-line text-lg"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <i className="ri-movie-2-line text-4xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500">
                  该分类暂无内容，请切换其他分类
                </p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer formUrl={formSubmitUrl} />
    </div>
  );
}